import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HpIcon, HpTag, TableScroll } from '@hp/ui';
import type { ClusterEndpoint as Endpoint, ClusterMember as Member, ClusterSummary as ClusterRow } from './queries';
import { RelativeTime } from '../../components/relative-time';
import { fmtDateTime, truncate } from '../../lib/format';
import { PdLink } from '../../components/pd-link';
import { renderValue } from './survivor';

function Empty() {
  return <span className="bo-muted">—</span>;
}

function EndpointCell({ list }: { list: Endpoint[] }) {
  const { t } = useTranslation('review');
  if (list.length === 0) return <Empty />;
  return (
    <ul className="bo-endpoints">
      {list.map((e, i) => {
        const shown = e.normValue ?? e.rawValue;
        const rawDiffers = e.rawValue !== null && e.normValue !== null && e.rawValue !== e.normValue;
        return (
          <li key={i}>
            <span className="bo-mono bo-nowrap">{shown ?? ''}</span>
            {e.isPrimary && (
              <span title={t('compare.primary')}>
                <HpIcon name="pin" className="bo-endpoints__primary" aria-label={t('compare.primary')} />
              </span>
            )}
            {e.label && <span className="bo-endpoints__label">{e.label}</span>}
            {rawDiffers && (
              <div className="bo-endpoints__raw bo-mono" title={t('compare.rawValue', { value: e.rawValue ?? '' })}>
                {e.rawValue}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface FieldRowProps {
  label: string;
  labelTitle?: string;
  members: Member[];
  render: (m: Member) => ReactNode;
  /** When set, cells are clickable and mark this member's value as the winner. */
  fieldKey?: string;
  overrides?: Record<string, number>;
  onToggle?: (fieldKey: string, pdId: number) => void;
}

function FieldRow({ label, labelTitle, members, render, fieldKey, overrides, onToggle }: FieldRowProps) {
  const { t } = useTranslation('review');
  return (
    <tr>
      <th scope="row" title={labelTitle ?? label}>
        {label}
      </th>
      {members.map((m) => {
        const clickable = fieldKey !== undefined && onToggle !== undefined;
        const overridden = fieldKey !== undefined && overrides?.[fieldKey] === m.pdId;
        const cls = [clickable ? 'is-clickable' : '', overridden ? 'is-override' : ''].join(' ').trim();
        return (
          <td
            key={m.pdId}
            className={cls || undefined}
            onClick={clickable ? () => onToggle(fieldKey, m.pdId) : undefined}
            title={clickable ? t('compare.keepValueTitle') : undefined}
            aria-selected={clickable ? overridden : undefined}
          >
            {render(m)}
          </td>
        );
      })}
    </tr>
  );
}

export function CompareTable({
  cluster,
  members,
  endpoints,
  pdDomain,
  survivor,
  defaultSurvivor,
  isPending,
  onSelectSurvivor,
  overrides,
  onToggleOverride,
}: {
  cluster: ClusterRow;
  members: Member[];
  endpoints: Endpoint[];
  pdDomain: string | null | undefined;
  survivor: number | null;
  defaultSurvivor: number | null;
  isPending: boolean;
  onSelectSurvivor: (pdId: number) => void;
  overrides: Record<string, number>;
  onToggleOverride: (fieldKey: string, pdId: number) => void;
}) {
  const { t } = useTranslation(['review', 'common']);
  const isPerson = cluster.entityType === 'person';

  const endpointsByMember = useMemo(() => {
    const map = new Map<number, { emails: Endpoint[]; phones: Endpoint[] }>();
    for (const e of endpoints) {
      let entry = map.get(e.pdId);
      if (!entry) {
        entry = { emails: [], phones: [] };
        map.set(e.pdId, entry);
      }
      (e.channel === 'email' ? entry.emails : entry.phones).push(e);
    }
    return map;
  }, [endpoints]);

  // custom field keys present (non-null) on at least one member
  const customKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const m of members) {
      if (!m.custom) continue;
      for (const [k, v] of Object.entries(m.custom)) {
        if (v !== null && v !== undefined) keys.add(k);
      }
    }
    return [...keys].sort();
  }, [members]);

  const rowProps = { members, overrides, onToggle: onToggleOverride };

  return (
    <section className="hp-card">
      <TableScroll label={t('compare.label')}>
        <table className="bo-compare">
          <thead>
            <tr>
              <th scope="col">{t('compare.survivorCol')}</th>
              {members.map((m) => (
                <th key={m.pdId} scope="col" className={survivor === m.pdId ? 'is-survivor' : undefined}>
                  <label className="bo-compare__pick">
                    <input
                      type="radio"
                      name="survivor"
                      checked={survivor === m.pdId}
                      onChange={() => onSelectSurvivor(m.pdId)}
                      disabled={!isPending}
                    />
                    <span>{survivor === m.pdId ? t('compare.survivor') : t('compare.keep')}</span>
                    {defaultSurvivor === m.pdId && (
                      <HpTag tone="info" title={t('compare.suggestedTitle')}>
                        {t('compare.suggested')}
                      </HpTag>
                    )}
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <FieldRow
              label={t('compare.pipedriveId')}
              members={members}
              render={(m) => (
                <PdLink
                  domain={pdDomain}
                  entity={cluster.entityType === 'organization' ? 'organization' : 'person'}
                  pdId={m.pdId}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            />
            <FieldRow
              label={t('common:table.name')}
              fieldKey="name"
              {...rowProps}
              render={(m) => m.name ?? <Empty />}
            />
            {isPerson ? (
              <>
                <FieldRow label={t('compare.organization')} members={members} render={(m) => m.orgName ?? <Empty />} />
                <FieldRow
                  label={t('compare.marketingStatus')}
                  members={members}
                  render={(m) => m.marketingStatus ?? <Empty />}
                />
              </>
            ) : (
              <>
                <FieldRow label={t('common:table.address')} members={members} render={(m) => m.address ?? <Empty />} />
                <FieldRow
                  label={t('compare.ico')}
                  members={members}
                  render={(m) =>
                    m.ico ? (
                      <span className="bo-mono bo-row">
                        {m.ico}
                        <span title={m.icoValid ? t('compare.icoValid') : t('compare.icoInvalid')}>
                          <HpIcon
                            name={m.icoValid ? 'circleCheck' : 'alert'}
                            className={m.icoValid ? 'bo-ico-valid' : 'bo-ico-invalid'}
                            aria-label={m.icoValid ? t('compare.icoValid') : t('compare.icoInvalid')}
                          />
                        </span>
                      </span>
                    ) : (
                      <Empty />
                    )
                  }
                />
              </>
            )}
            <FieldRow
              label={t('compare.emails')}
              members={members}
              render={(m) => <EndpointCell list={endpointsByMember.get(m.pdId)?.emails ?? []} />}
            />
            <FieldRow
              label={t('compare.phones')}
              members={members}
              render={(m) => <EndpointCell list={endpointsByMember.get(m.pdId)?.phones ?? []} />}
            />
            <FieldRow
              label={t('compare.deals')}
              members={members}
              render={(m) => (
                <span className="bo-mono">
                  {m.openDeals} / {m.closedDeals}
                </span>
              )}
            />
            {isPerson && (
              <>
                <FieldRow
                  label={t('compare.doneActivities')}
                  members={members}
                  render={(m) => <span className="bo-mono">{m.doneActivities ?? 0}</span>}
                />
                <FieldRow
                  label={t('compare.lastActivity')}
                  members={members}
                  render={(m) => <RelativeTime value={m.lastActivityAt} />}
                />
              </>
            )}
            {!isPerson && (
              <FieldRow
                label={t('compare.people')}
                members={members}
                render={(m) => <span className="bo-mono">{m.peopleCount ?? 0}</span>}
              />
            )}
            <FieldRow
              label={t('compare.added')}
              members={members}
              render={(m) => (
                <span title={fmtDateTime(m.addTime)}>
                  <RelativeTime value={m.addTime} />
                </span>
              )}
            />
            <FieldRow
              label={t('common:table.updated')}
              members={members}
              render={(m) => <RelativeTime value={m.updateTime} />}
            />
            <FieldRow
              label={t('compare.filledFields')}
              members={members}
              render={(m) => <span className="bo-mono">{m.filledFields}</span>}
            />
            {customKeys.map((key) => (
              <FieldRow
                key={key}
                label={key.length > 8 ? `${key.slice(0, 8)}…` : key}
                labelTitle={key}
                fieldKey={key}
                {...rowProps}
                render={(m) => {
                  const v = renderValue(m.custom?.[key]);
                  return v ? (
                    <span className="bo-break" title={v}>
                      {truncate(v, 120)}
                    </span>
                  ) : (
                    <Empty />
                  );
                }}
              />
            ))}
          </tbody>
        </table>
      </TableScroll>
    </section>
  );
}
